import { useCallback, useEffect, useRef, useState } from "react";
import msg from "components/string-table";
import { Colors, Icon } from "main";

import styles from "./SwingHeroScoreProgress.module.css";

type SwingHeroScoreProgressProps = {
  score: number;
};

export function SwingHeroScoreProgress(props: SwingHeroScoreProgressProps) {
  const { score } = props;
  // Score is coming in as a float, so we need to scale it
  const scaledScore = score * 100;
  const displayedScore = scaledScore > 100 ? "100+" : scaledScore.toFixed(1);

  const [progressBarWidth, setProgressBarWidth] = useState(0);
  const [badgePosition, setBadgePosition] = useState(0);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Get the width of the progress bar since it is determined by the parent component.
  const updateWidth = useCallback(() => {
    if (progressBarRef.current) {
      const width = progressBarRef.current.offsetWidth;
      setProgressBarWidth(width);
      // Swing Hero badge is positioned at roughly 87% of the total progress bar width - rough estimate taken designs.
      setBadgePosition(width * 0.87);
    }
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    const progressBarElement = progressBarRef.current;

    if (progressBarElement) {
      resizeObserver.observe(progressBarElement);
    }

    return () => {
      if (progressBarElement) {
        resizeObserver.unobserve(progressBarElement);
      }
    };
  }, [updateWidth]);

  const progressColor = getScoreColor(scaledScore);

  // Calculate the progress bar fill based on the badge position reflecting 100%
  let progressFillWidth;
  if (scaledScore >= 95) {
    progressFillWidth = 100;
  } else {
    // Calculate the ratio of scaledScore to maximum score
    const scoreRatio = scaledScore / 95;
    // Gradually apply an offset as the score ratio exceeds 90%, reaching a maximum of -14 when close to 100%
    const dynamicOffset = scoreRatio > 0.9 ? (scoreRatio - 0.9) * 140 : 0;
    progressFillWidth = scoreRatio * ((badgePosition - dynamicOffset) / progressBarWidth) * 100;
  }

  return (
    <div className={styles.container}>
      <p className={styles.title}>{msg("SWING_HERO_SCORE_PROGRESS_TITLE")}</p>
      <p className={styles.score}>{displayedScore}</p>
      <div className={styles.progressBar} ref={progressBarRef}>
        <div
          className={styles.progress}
          style={{
            width: `${progressFillWidth}%`,
            backgroundColor: progressColor,
          }}
        ></div>
        <div
          className={styles.swingHeroBadge}
          style={{
            left: `${badgePosition}px`,
          }}
        >
          <Icon name="Swing Hero" />
          <span
            className={styles.swingHero}
          >{`${msg("SUB_PROFILE_CONTACT_SWING")} ${msg("SUB_PROFILE_CONTACT_HERO")}`}</span>
        </div>
      </div>
    </div>
  );
}

function getScoreColor(score: number) {
  if (score >= 95) return Colors.green500;
  if (score >= 80) return Colors.blue500;
  return Colors.yellow600;
}
